import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  Boxes,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  MailCheck,
  QrCode,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";

// KRAYA_2FA_422_GUARD_V1
function normalizeApiError(error, fallback = "Terjadi kesalahan. Silakan coba kembali.") {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) return detail.trim();

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (!item || typeof item !== "object") return "";
        const field = Array.isArray(item.loc) ? item.loc.filter((part) => part !== "body").join(".") : "";
        if (field === "code" && item.type === "string_too_short") {
          const min = item?.ctx?.min_length || 6;
          return `Kode Google Authenticator / recovery code minimal ${min} karakter.`;
        }
        const message = item.msg || item.message || "";
        return field && message ? `${field}: ${message}` : message;
      })
      .filter(Boolean);
    if (messages.length) return messages.join(" ");
  }

  if (detail && typeof detail === "object") return detail.message || detail.msg || fallback;
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();
  return fallback;
}

export default function Login() {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [accountLocked, setAccountLocked] = useState(false);
  const [twoFactor, setTwoFactor] = useState({ required: false, challengeToken: "", code: "" });

  const finishLogin = (result) => {
    const signedInUser = result?.user;
    if (!signedInUser) throw new Error("Data pengguna tidak tersedia setelah login");
    toast.success(`Selamat datang, ${signedInUser.name}`);
    navigate(signedInUser.role === "platform_owner" ? "/platform" : "/app");
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");
    setAccountLocked(false);
    try {
      if (twoFactor.required) {
        const normalizedTwoFactorCode = twoFactor.code.trim();
        if (normalizedTwoFactorCode.length < 6) {
          const message = "Masukkan kode Google Authenticator 6 digit atau recovery code yang valid.";
          setErrorMessage(message);
          toast.error(message);
          return;
        }
        const result = await verifyTwoFactor(twoFactor.challengeToken, normalizedTwoFactorCode);
        finishLogin(result);
        return;
      }

      const result = await login(form.identifier.trim(), form.password);
      if (result?.requires_2fa) {
        setTwoFactor({ required: true, challengeToken: result.challenge_token, code: "" });
        setForm((current) => ({ ...current, password: "" }));
        toast.message("Password benar. Masukkan kode Google Authenticator.");
        return;
      }
      finishLogin(result);
    } catch (error) {
      const message = normalizeApiError(error, "Login gagal. Periksa kembali data Anda.");
      setErrorMessage(message);
      setAccountLocked(error?.response?.status === 423 && !twoFactor.required);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const resetTwoFactorStep = () => {
    setTwoFactor({ required: false, challengeToken: "", code: "" });
    setErrorMessage("");
    setAccountLocked(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <div className="grid min-h-screen lg:grid-cols-[1.08fr_.92fr]">
        <section className="relative hidden overflow-hidden bg-slate-950 p-12 text-white lg:flex lg:flex-col lg:justify-between xl:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(37,99,235,.35),transparent_30%),radial-gradient(circle_at_85%_80%,rgba(34,211,238,.18),transparent_32%)]" />
          <Link to="/" className="relative w-fit" data-testid="login-brand"><BrandMark size="lg" variant="on-dark" /></Link>

          <div className="relative max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[.16em] text-cyan-200"><Sparkles className="h-3.5 w-3.5" /> Workspace bisnis Anda</div>
            <h1 className="mt-7 max-w-xl text-6xl font-black leading-[.95] tracking-[-0.055em]">Semua outlet. Satu akses. Satu kendali.</h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-300">Masuk ke KRAYA untuk mengelola kasir, stok, QRIS, laporan, keuangan, dan aktivitas tim dari satu workspace.</p>

            <div className="mt-9 grid grid-cols-2 gap-3">
              {[
                [BarChart3, "Dashboard bisnis"],
                [Boxes, "Stok real-time"],
                [QrCode, "QRIS outlet"],
                [ShieldCheck, "2FA & audit"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[.05] p-4 text-sm font-semibold text-slate-200"><Icon className="h-5 w-5 text-cyan-300" />{label}</div>
              ))}
            </div>

            <div className="mt-5 rounded-[2rem] border border-white/10 bg-white/[.05] p-5 backdrop-blur">
              <div className="flex items-center justify-between"><div><div className="text-[10px] font-bold uppercase tracking-[.15em] text-slate-500">Ringkasan hari ini</div><div className="mt-1 text-xl font-black">Rp8,4 juta</div></div><div className="rounded-xl bg-emerald-400/10 px-3 py-1.5 text-xs font-bold text-emerald-300">+12,4%</div></div>
              <div className="mt-5 flex h-16 items-end gap-2">{[42, 55, 48, 72, 62, 84, 96].map((height, index) => <div key={index} className="flex-1 rounded-t bg-gradient-to-t from-blue-600 to-cyan-300" style={{ height: `${height}%` }} />)}</div>
            </div>
          </div>
          <div className="relative text-xs text-slate-500">KRAYA. · Powered By AntSoft ID</div>
        </section>

        <section className="flex min-h-screen items-center justify-center p-5 sm:p-8 lg:p-12">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-between lg:hidden">
              <Link to="/" data-testid="login-brand-mobile"><BrandMark /></Link>
              <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-950"><ArrowLeft className="h-3.5 w-3.5" /> Beranda</Link>
            </div>

            <div className="mt-8 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_30px_80px_-45px_rgba(15,23,42,.45)] sm:p-9 lg:mt-0">
              <div className="hidden items-center justify-between lg:flex">
                <Link to="/" className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-slate-950"><ArrowLeft className="h-3.5 w-3.5" /> Kembali ke beranda</Link>
                <div className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.14em] text-emerald-700">Secure access</div>
              </div>

              <div className="mt-7 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                {twoFactor.required ? <KeyRound className="h-6 w-6" /> : <LockKeyhole className="h-6 w-6" />}
              </div>
              <h2 className="mt-5 text-4xl font-black tracking-[-0.045em]">{twoFactor.required ? "Verifikasi 2FA" : "Selamat datang kembali"}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500">
                {twoFactor.required ? "Masukkan kode Google Authenticator 6 digit atau recovery code yang valid." : "Masuk menggunakan email atau nomor handphone yang terdaftar."}
              </p>

              {errorMessage && (
                <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800" role="alert" data-testid="login-error-message">
                  <div className="flex gap-3"><AlertCircle className="mt-0.5 h-5 w-5 shrink-0" /><span>{errorMessage}</span></div>
                  {!twoFactor.required && errorMessage.toLowerCase().includes("verifikasi") && form.identifier.includes("@") && (
                    <Link to={`/verify-email?email=${encodeURIComponent(form.identifier.trim())}`} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-rose-700 shadow-sm ring-1 ring-rose-200"><MailCheck className="h-4 w-4" /> Kirim ulang email verifikasi</Link>
                  )}
                  {accountLocked && (
                    <Link to={form.identifier.includes("@") ? `/forgot-password?email=${encodeURIComponent(form.identifier.trim())}` : "/forgot-password"} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-rose-700 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-rose-800"><LockKeyhole className="h-4 w-4" /> Reset Password & Buka Lock</Link>
                  )}
                </div>
              )}

              <form onSubmit={submit} className="mt-7 space-y-5">
                {!twoFactor.required ? (
                  <>
                    <div>
                      <label className="mb-2 block text-sm font-bold text-slate-700">Email atau nomor HP</label>
                      <input data-testid="login-identifier-input" value={form.identifier} onChange={(event) => setForm({ ...form, identifier: event.target.value })} required autoComplete="username" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="nama@bisnis.com atau 0812xxxx" />
                    </div>
                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3"><label className="block text-sm font-bold text-slate-700">Password</label><Link to="/forgot-password" className="text-xs font-bold text-blue-600 hover:text-blue-700">Lupa password?</Link></div>
                      <div className="relative">
                        <input data-testid="login-password-input" type={showPassword ? "text" : "password"} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} required autoComplete="current-password" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 pr-12 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="Masukkan password" />
                        <button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-slate-400 transition hover:text-slate-700" aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}>{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="mb-2 block text-sm font-bold text-slate-700">Kode autentikasi</label>
                    <input data-testid="login-2fa-input" value={twoFactor.code} onChange={(event) => setTwoFactor((current) => ({ ...current, code: event.target.value }))} required autoComplete="one-time-code" inputMode="text" className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5 text-center text-lg font-black tracking-[.2em] outline-none transition placeholder:tracking-normal placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100" placeholder="000000 / recovery code" />
                    <button type="button" onClick={resetTwoFactorStep} className="mt-3 text-xs font-bold text-slate-500 hover:text-slate-950">← Gunakan akun lain / ulangi login</button>
                  </div>
                )}

                <button type="submit" data-testid="login-submit-btn" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 py-3.5 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50">
                  {loading ? "Memverifikasi..." : twoFactor.required ? "Verifikasi & Masuk" : "Masuk ke KRAYA"}
                </button>
              </form>

              {!twoFactor.required && <div className="mt-7 border-t border-slate-200 pt-6 text-center text-sm text-slate-500">Belum punya akun? <Link to="/register" className="font-bold text-blue-600 hover:text-blue-700" data-testid="login-to-register">Daftar & mulai trial</Link></div>}
              <div className="mt-6 flex items-center justify-center gap-2 text-[11px] font-semibold text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Login aman dengan dukungan Google Authenticator 2FA</div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
